import styled from "styled-components";
import { media, breakpoints } from "../../helpers/responsive";
import TickerBanner from "../tickerBanner";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { HiChevronLeft } from "react-icons/hi";
import { useRef, useContext } from "react";
import { DashboardEventsContext } from "../dashboardEvents";
import { IoReload } from "react-icons/io5";
import SecondaryFilterDialog from "../secondaryFilterDropdown";
import ComparisonSelect from "../../features/comparison/components/ComparisonSelect";
import ComparisonDimensionFilter from "../../features/comparison/components/ComparisonDimensionFilter";
import { useComparisonState } from "../../features/comparison/hooks/useComparisonState";
import LocationFilterDropdown from "../locationFilterDropdown";
import { useFilterNavigate } from "../../helpers/hooks/useFilterNavigate";
import { useParams } from "react-router-dom";
import { useEventListener } from "usehooks-ts";
import LiveBadge from "./liveBadge";
import DateRangePicker from "../dateRangePicker";

interface IOverviewHeaderProps {
  dashboardTitle: string;
  analyticsDashboardTitle?: string;
  isComparisonEnabled?: boolean;
  comparisonDimensions?: any;
  shouldFreezeDateRangeSelection?: boolean;
  refreshData?: () => void;
  isMaintenance?: boolean;
}

const OverviewHeader = ({
  dashboardTitle = "",
  analyticsDashboardTitle = "",
  isComparisonEnabled = false,
  comparisonDimensions = null,
  shouldFreezeDateRangeSelection = false,
  refreshData,
  isMaintenance,
}: IOverviewHeaderProps) => {
  const companyLocations = useSelector((state: RootState) => state.locations);
  const { isLiveDashboard } = useContext(DashboardEventsContext);
  const comparisonState = useComparisonState();

  const pageHeaderRef = useRef<HTMLDivElement>(null);

  const navigateTo = useFilterNavigate();
  const routeMatch = useParams();

  const showSecondaryFilters =
    analyticsDashboardTitle || comparisonState.isActive;
  const isAnalyticsView =
    !comparisonState.isActive && analyticsDashboardTitle;
  const isDashboardView =
    !comparisonState.isActive && !analyticsDashboardTitle;
  const isComparisonResultsView =
    comparisonState.isActive && analyticsDashboardTitle;
  const showLiveBadge =
    !isAnalyticsView || (isAnalyticsView && shouldFreezeDateRangeSelection);
  const showLocationFilters = companyLocations.length > 0;
  const showRefreshDataControl = false; // Disabled on feedback. Boolean(refreshData)

  const handleAnalyticsBackNavigation = () => {
    navigateTo(`/${routeMatch.dashboard}`);
  };

  const handlePageScrollEvent = () => {
    if (pageHeaderRef.current) {
      let header = pageHeaderRef.current;
      let sticky = header.offsetTop;
      if (window.scrollY > sticky - 50) {
        header.classList.add("sticky-header");
      } else {
        header.classList.remove("sticky-header");
      }
    }
  };

  useEventListener("scroll", handlePageScrollEvent);

  return (
    <Wrapper ref={pageHeaderRef} style={{ top: isMaintenance ? "120px" : "60px" }}>
      <TickerBanner />
      <div className="content">
        <div className="overview-title-nav-section">
          <span className="nav-section">
            <span
              className={`dashboard-title ${
                isDashboardView && !isAnalyticsView
                  ? "dashboard-only"
                  : !comparisonState.isActive
                  ? "show-link"
                  : ""
              }`}
              onClick={handleAnalyticsBackNavigation}>
              <HiChevronLeft
                className={`back-navigation ${
                  !analyticsDashboardTitle ? "hide" : ""
                }`}
              />
              {isDashboardView && !isAnalyticsView
                ? `${dashboardTitle} Insights`
                : dashboardTitle}
            </span>
            {analyticsDashboardTitle ? (
              <span className="analytics-dashboard-title">
                {analyticsDashboardTitle}
              </span>
            ) : null}
          </span>
          <span
            className={`live-controls ${
              isDashboardView && !isAnalyticsView ? "dashboard-only" : null
            }`}>
            {showLiveBadge && isLiveDashboard ? (
              <LiveBadge isLive={isLiveDashboard} />
            ) : null}
            {showRefreshDataControl ? (
              <span className="refresh-data-cta" onClick={() => refreshData()}>
                <IoReload fontSize={"12px"} />
                <span>Refresh Data</span>
              </span>
            ) : null}
          </span>
        </div>
        <div className="overview-controls">
          {showSecondaryFilters ? <SecondaryFilterDialog /> : null}
          {showLocationFilters ? <LocationFilterDropdown /> : null}
          {!isComparisonResultsView ? (
            <DateRangePicker freeze={shouldFreezeDateRangeSelection} />
          ) : null}
          {isComparisonEnabled ? (
            isAnalyticsView || isComparisonResultsView ? (
              <div className={`comparison ${isComparisonResultsView ? "comparison-active" : ""}`}>
                <ComparisonSelect />
                {comparisonState.isActive && comparisonDimensions ? (
                  <ComparisonDimensionFilter dimensions={comparisonDimensions} />
                ) : null}
              </div>
            ) : null
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default OverviewHeader;

const Wrapper = styled.div`
  position: fixed;
  z-index: 1000;
  background: ${({ theme }) => theme.body};
  width: 100%;
  height: 150px;
  padding: 0 50px 0 50px;
  transition: all 500ms cubic-bezier(0.095, 0.985, 0.045, 0.995);

  .content {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: calc(100% - 45px);

    .overview-title-nav-section {
      display: flex;
      flex: 1;
      min-width: 0;
      align-items: center;
      gap: 10px;

      .nav-section {
        display: flex;
        flex-direction: column;
        min-width: 0;

        .dashboard-title {
          display: flex;
          align-items: center;
          font-size: 20px;
          font-weight: 600;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &.dashboard-only {
            font-size: 2.2rem;
            font-weight: 900;
            line-height: 2.1;
          }
          &.show-link { cursor: pointer; }

          .back-navigation {
            flex-shrink: 0;
            cursor: pointer;
            margin-right: 2px;
            &.hide { display: none; }
          }
        }

        .analytics-dashboard-title {
          font-size: 1.5rem;
          font-weight: 900;
          line-height: 2.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-left: 22px;
        }
      }

      .live-controls {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .refresh-data-cta {
          display: flex;
          align-items: center;
          cursor: pointer;
          color: #ffffff;
          border-radius: 20px;
          padding: 3px;
          height: 23px;
          width: 23px;
          background-color: ${({ theme }) => theme.button.primary_color};
          margin-left: 10px;
          > span {
            margin-left: 2px;
            font-size: 10px;
            display: inline-block;
            overflow: hidden;
            white-space: nowrap;
            width: 0px;
            transition: all 500ms cubic-bezier(0.18, 1, 0.13, 0.965);
          }
          > svg {
            font-size: 15px;
            margin-left: 2px;
            transition: all 500ms cubic-bezier(0.18, 1, 0.13, 0.965);
          }
          &:hover {
            width: 90px;
            > span { width: 60px; }
            > svg { transform: rotate(360deg); }
          }
        }
      }
    }

    .overview-controls {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: 10px;

      > .comparison {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }

  /* ═══ STICKY ═══ */
  &.sticky-header {
    height: 100px;
    background: ${({ theme }) => theme.sticky_nav};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
    transition: all 500ms cubic-bezier(0.095, 0.985, 0.045, 0.995);

    .content {
      .overview-title-nav-section .nav-section {
        flex-direction: row;
        align-items: baseline;
        gap: 6px;

        .dashboard-title {
          font-size: 18px;
          &.dashboard-only {
            font-size: 18px;
            &::after { content: ""; }
          }
          &::after { content: " /"; }
        }
        .analytics-dashboard-title {
          font-size: 18px;
          font-weight: 800;
          padding-left: 0;
        }
      }

      .overview-controls {
        .secondary-filter-control .filter-icon {
          border: 1px solid ${({ theme }) => theme.border_color};
        }
        .location-select-control .dropdown .dropdown-toggle {
          border: 1px solid ${({ theme }) => theme.border_color};
        }
        .date-range-select-control .date-range-select-toggle {
          border: 1px solid ${({ theme }) => theme.border_color};
        }
      }
    }
  }

  /* ═══ 2-ROW (<1024px) ═══
     Row 1: Title (left aligned)
     Row 2: Controls in single row (right aligned)
     D-level: Location + Datepicker
     A-level: SecondaryFilter + Location + Datepicker + Comparison */
  @media (max-width: 1023px) {
    height: auto;
    min-height: 100px;
    padding: 0 24px 12px 24px;

    .content {
      flex-wrap: wrap;
      align-items: center;
      height: auto;
      padding-bottom: 0;
      gap: 8px;

      .overview-title-nav-section {
        flex: 1 1 100%;
        align-items: center;
        .nav-section {
          .dashboard-title.dashboard-only { font-size: 1.5rem; }
          .analytics-dashboard-title { font-size: 1.1rem; padding-left: 16px; }
        }
      }
      .overview-controls {
        flex: 1 1 100%;
        flex-wrap: nowrap;
        justify-content: flex-end;

        /* Override child full-width styles from media.down.xs (<768px) */
        > .location-select-control {
          .dropdown { width: auto; }
          .dropdown .dropdown-toggle { width: auto; max-width: 250px; }
        }
        > .date-range-select-control {
          width: auto;
          .date-range-select-toggle { width: auto; min-width: auto; }
        }
      }
    }

    &.sticky-header {
      height: auto;
      min-height: 60px;
      padding: 0 24px 10px 24px;
    }
  }

  /* ═══ 3-ROW (<720px) ═══
     Row 1: Title (left aligned)
     D-level → Row 2: Location (fill) | Row 3: Datepicker (fill)
     A-level → Row 2: SecondaryFilter + Location (fill) | Row 3: Datepicker (fill) + Comparison */
  @media (max-width: 719px) {
    padding: 0 16px 10px 16px;

    .content {
      .overview-title-nav-section .nav-section {
        .dashboard-title {
          font-size: 15px;
          &.dashboard-only { font-size: 1.2rem; }
        }
        .analytics-dashboard-title { font-size: 0.95rem; padding-left: 8px; }
      }

      .overview-controls {
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 6px;

        /* Row 2: filter icon (auto) + location (fill remaining width) */
        > .secondary-filter-control { flex: 0 0 auto; }
        > .location-select-control {
          flex: 1 1 50%;
          min-width: 0;
          .dropdown { width: 100%; }
          .dropdown .dropdown-toggle { width: 100%; max-width: 100%; }
        }

        /* Row 3: datepicker (fill) + comparison icon (auto) */
        > .date-range-select-control {
          flex: 1 1 50%;
          min-width: 0;
          width: 100%;
          .date-range-select-toggle { width: 100%; min-width: auto; }
        }
        > .comparison { flex: 0 0 auto; }

        /* Row 4 (comparison active mode): full width */
        > .comparison.comparison-active {
          flex: 1 1 100%;
          display: flex;
          align-items: stretch;
          gap: 8px;
          > button, > div > button { height: 40px; }
          > button:first-child { flex: 1 1 auto; }
        }
      }
    }

    &.sticky-header {
      padding: 0 16px 8px 16px;
      .content .overview-title-nav-section .nav-section {
        .dashboard-title {
          font-size: 14px;
          &.dashboard-only { font-size: 14px; }
        }
        .analytics-dashboard-title { font-size: 14px; }
      }
    }
  }

  /* ═══ 4K (>=2560px) ═══ */
  ${media.up.xxl} {
    padding: 0 80px 0 80px;
    .content .overview-title-nav-section .nav-section {
      .dashboard-title.dashboard-only { font-size: 3rem; }
      .analytics-dashboard-title { font-size: 2.5rem; }
    }
    &.sticky-header {
      padding: 0 80px 0 80px;
    }
  }
`;